import _ from "lodash";
import React, { cloneElement, useEffect, useRef, useState } from "react";
import classes from "./CheckListEditRowWithReordering.scss";
import TextAreaInput from "UIComponents/TextAreaInput";
import I18nHOC from "UIComponents/I18nHOC";
import { colors, fontStyle } from "Constants";
import { Dialog, IconButton } from "@toddle-edu/ds-web";
import {
  AddColumnOutlined,
  DeleteOutlined,
  DotsSixVerticalOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { elevations } from "@toddle-edu/ds-theme";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { getItemBeforeAndAfter } from "Utils/Common";
import { Popover } from "@toddle-edu/react-tiny-popover";
import { useIntersectionObserver } from "Utils";
import { useI18n } from "Hooks";

const inputStyle = {
  cell: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    padding: "0 24px 0 0",
    color: colors.textDefault,
    lineHeight: 1.57,
    ...fontStyle.medium,
    fontSize: "1.4rem",
    overflow: "hidden",
  },
  disbledCell: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    lineHeight: 1.57,
    ...fontStyle.medium,
    fontSize: "1.4rem",
    color: colors.textDisabled,
    padding: 16,
  },
  textAreaContainerStyle: {
    flexDirection: "row",
  },
};

const styles = {
  popoverContainerStyle: {
    zIndex: 1100,
  },
  popoverIconStyle: {
    transform: "rotate(90deg)",
    width: "20px",
    height: "16px",
  },
  rowPopoverContainerStyle: {
    zIndex: 1100,
    top: "-10px",
    left: "-10px",
  },
  columnPopoverContainerStyle: {
    zIndex: 1100,
    top: "-10px",
    left: "-5px",
  },
  rotateIconStyle: {
    transform: "rotate(90deg)",
  },
  scaleIconStyle: {
    transform: "scaleX(-1)",
  },
  transformIconStyle: {
    transform: "rotate(90deg) scaleX(-1)",
  },
};

const mergedStyles = {
  onDraggingTextAreaStyles: {
    ...inputStyle.cell,
    cursor: "grabbing !important",
  },
};

const DeleteModal = ({ toggleDeleteModal, onConfirmDelete, selectedType }) => {
  const { t } = useI18n();

  return (
    <Dialog
      variant="delete-dialog"
      title={t("common:delete_with_label", {
        label: t(`common:${selectedType}`),
      })}
      message={t("common:delete_msg_with_label", {
        label: t(`common:${selectedType}`),
      })}
      buttonOneText={t("common:cancel")}
      buttonTwoText={t("common:delete")}
      onButtonOneClick={toggleDeleteModal}
      onButtonTwoClick={onConfirmDelete}
      isClosable={true}
      onClose={toggleDeleteModal}
    />
  );
};

const DraggableColumn = ({
  id,
  children,
  projectedLineData,
  selectedChecklist,
  options,
}) => {
  const disableDragging = _.some(options, option =>
    _.includes(_.get(option, "id", null), "NEW")
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    node,
  } = useDraggable({
    id,
    disabled: selectedChecklist.type === "row" || disableDragging,
  });
  let style = {
    transform: !_.isEmpty(transform)
      ? `translate3d(${transform.x}px, ${transform.y}px, 0px)`
      : "",
    transition,
    zIndex: isDragging ? 2500 : 1,
    cursor: isDragging ? "grabbing" : "",
    opacity: isDragging ? 0.75 : "",
  };

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id,
    disabled: selectedChecklist.type === "row" || disableDragging,
  });
  if (projectedLineData?.id === id) {
    style = { ...style, ...projectedLineData.style };
  }

  return (
    <div
      ref={ref => {
        setNodeRef(ref);
        setDroppableNodeRef(ref);
      }}
      className={classes.columnItem}
      style={style}
    >
      {cloneElement(children, { node, attributes, listeners, disableDragging })}
    </div>
  );
};

const CheckListOption = ({
  label,
  writeCheckListElement,
  index,
  id,
  shouldDisable,
  onColumnFocus,
  setFocusedCell,
  setCellRef,
  criteriaId,
  item,
  t,
  onOptionDeleteClick,
  showHoverState,
  writeChecklistElementLocal,
  assignmentId,
  saveAssessmentToolUpdateStatusInRedux,
  hoverChecklistDetails,
  selectedChecklist,
  setSelectedChecklist,
  setHoverChecklistDetails,
  checklistItems,
  criteriaIndex,
  createEmptyCheckListOptionItem,
  options,
  selectedChecklistIndex,
  isDragging,
  attributes,
  listeners,
  node,
  showOptionDeleteIcon,
  setIndexToFocusData,
  indexToFocusData,
  disableDragging,
}) => {
  const [optionLabel, setOptionLabel] = useState(label);
  const columnActionsRef = useRef(null);
  const textAreaRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toggleDeleteModal = () => setShowDeleteModal(prev => !prev);
  const onConfirmDelete = () => {
    onOptionDeleteClick(id);
    setSelectedChecklist({});
    toggleDeleteModal();
    if (index > 0) setIndexToFocusData({ type: "column", index: index - 1 });
    else setIndexToFocusData({ type: "column", index });
  };
  const isFullyVisible = useIntersectionObserver(node, {
    threshold: 0.8,
  });
  // check if the selected item is in the view or not
  useEffect(() => {
    if (
      !isFullyVisible &&
      !isDragging &&
      selectedChecklist.type === "column" &&
      selectedChecklist.id === id
    ) {
      setSelectedChecklist({});
    }
  }, [isFullyVisible]);

  const refKey = `Option_${id}_Criteria_${criteriaId}`;

  const disabled = _.includes(item, "NEW");
  const disableCreateNewOptions = _.size(options) === 4;

  const optionClass = classNames({
    [classes.option]: true,
    [classes.optionDisabled]: shouldDisable,
    [classes.optionHover]: showHoverState && !isDragging,
    [classes.selectedOption]:
      selectedChecklistIndex === index &&
      criteriaIndex > 0 &&
      criteriaIndex < _.size(checklistItems) - 1,
    [classes.lastSelectedOption]:
      selectedChecklistIndex === index &&
      criteriaIndex === _.size(checklistItems) - 1,
    [classes.firstSelectedOption]:
      selectedChecklistIndex === index && criteriaIndex === 0,
    [classes.selectedRowOption]:
      selectedChecklist.type === "row" && selectedChecklist.id === criteriaId,
    [classes.draggingCell]:
      isDragging && selectedChecklistIndex === index && !shouldDisable,
  });

  const updateChecklistOption = value => {
    setOptionLabel(value);
    writeCheckListElement({
      id: id,
      label: value,
      type: "CHECKLIST_OPTION",
      elementDetails: item,
    });
    saveAssessmentToolUpdateStatusInRedux({
      assignmentId,
      toolType: "CHECKLIST",
    });
  };

  const columnActionsClass = classNames({
    [classes.columnActions]: true,
    [classes.activeColumnActions]: _.get(selectedChecklist, "id", null) === id,
  });

  const onAddChecklistOptionAfter = async () => {
    setSelectedChecklist({});
    await createEmptyCheckListOptionItem({ putAfterItemId: id });
    setIndexToFocusData({ type: "column", index: index + 1 });
  };

  const onAddChecklistOptionBefore = async () => {
    const { before } = getItemBeforeAndAfter(options, id);
    setSelectedChecklist({});
    await createEmptyCheckListOptionItem({
      putAfterItemId: before?.id || null,
    });
    setIndexToFocusData({ type: "column", index });
  };

  const enableActionPopover =
    node.current &&
    isFullyVisible &&
    !shouldDisable &&
    (hoverChecklistDetails.checklistOptionIndex === index ||
      id === selectedChecklist.id) &&
    !isDragging;

  const enableActionPopoverOption =
    id === selectedChecklist.id &&
    columnActionsRef.current &&
    isFullyVisible &&
    !isDragging;

  if (indexToFocusData.index === index && indexToFocusData.type === "column") {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }

  return (
    <div
      onFocus={() => {
        onColumnFocus ? onColumnFocus(id) : null;
      }}
      className={optionClass}
      onMouseEnter={() => {
        setHoverChecklistDetails({
          checklistItemId: criteriaId,
          checklistOptionIndex: index,
        });
      }}
      onMouseLeave={() => {
        setHoverChecklistDetails({});
      }}
    >
      <TextAreaInput
        disabled={shouldDisable}
        value={shouldDisable ? label : optionLabel}
        textAreaStyles={
          shouldDisable
            ? inputStyle.disbledCell
            : isDragging
            ? mergedStyles.onDraggingTextAreaStyles
            : inputStyle.cell
        }
        ref={textAreaRef}
        updateInputRef={ref => setCellRef({ ref, key: refKey })}
        onBlurInputField={() => {
          if (!_.isEmpty(indexToFocusData)) setIndexToFocusData({});
          setFocusedCell(null);
          writeChecklistElementLocal({
            id: id,
            label: optionLabel,
            type: "CHECKLIST_OPTION",
            elementDetails: item,
          });
        }}
        onFocusInputField={() => {
          setFocusedCell(refKey);
          setSelectedChecklist({});
        }}
        placeholder={`${t("common:option")} ${index + 1}`}
        updateInputField={updateChecklistOption}
        className={classes.optionInput}
        minRows={1}
        textAreaContainerStyle={inputStyle.textAreaContainerStyle}
      />
      {enableActionPopover ? (
        <Popover
          isOpen={true}
          align={"center"}
          positions={["top"]}
          containerStyle={styles.columnPopoverContainerStyle}
          boundaryInset={20}
          reposition={true}
          content={() => {
            return (
              <div {...attributes} {...listeners}>
                <div
                  id={"CHECKLIST_OPTION"}
                  className={columnActionsClass}
                  ref={columnActionsRef}
                  onClick={() => {
                    setSelectedChecklist(
                      _.get(selectedChecklist, "id", null) === id
                        ? {}
                        : {
                            id,
                            type: "column",
                          }
                    );
                  }}
                >
                  <DotsSixVerticalOutlined
                    size={"xx-small"}
                    style={styles.popoverIconStyle}
                  />
                </div>
              </div>
            );
          }}
          childrenRef={{ current: node.current }}
          isChildrenRefPassed={true}
        />
      ) : null}
      {enableActionPopoverOption ? (
        <Popover
          isOpen={true}
          align={"center"}
          boundaryInset={50}
          positions={["bottom"]}
          containerStyle={styles.popoverContainerStyle}
          reposition={true}
          onClickOutside={() => setSelectedChecklist({})}
          content={() => {
            return (
              <div
                className={classes.popoverActions}
                style={elevations["elevation-2B"]}
              >
                <IconButton
                  dsVersion="2.0"
                  icon={<AddColumnOutlined variant="subtle" />}
                  size={"small"}
                  variant="neutral"
                  type="plain"
                  style={styles.scaleIconStyle}
                  onClick={onAddChecklistOptionBefore}
                  disabled={
                    disabled || disableCreateNewOptions || disableDragging
                  }
                />

                <IconButton
                  dsVersion="2.0"
                  icon={<AddColumnOutlined />}
                  size={"small"}
                  variant="neutral"
                  type="plain"
                  onClick={onAddChecklistOptionAfter}
                  disabled={
                    disabled || disableCreateNewOptions || disableDragging
                  }
                />

                {showOptionDeleteIcon ? (
                  <>
                    <div className={classes.popoverActionsSeparator} />

                    <IconButton
                      dsVersion="2.0"
                      icon={<DeleteOutlined variant="critical" />}
                      size={"small"}
                      variant="neutral"
                      type="plain"
                      onClick={toggleDeleteModal}
                      disabled={disabled || disableDragging}
                    />
                  </>
                ) : null}
                {showDeleteModal ? (
                  <DeleteModal
                    toggleDeleteModal={toggleDeleteModal}
                    onConfirmDelete={onConfirmDelete}
                    selectedType={"column"}
                  />
                ) : null}
              </div>
            );
          }}
          childrenRef={{
            current: columnActionsRef.current,
          }}
          isChildrenRefPassed={true}
        />
      ) : null}
    </div>
  );
};

const CheckListEditRow = props => {
  const {
    id,
    label,
    options,
    index,
    minOptionCount,
    onOptionDeleteClick,
    onItemDeleteClick,
    writeCheckListElement,
    onColumnFocus,
    onRowFocus,
    setCellRef,
    setFocusedCell,
    item,
    t,
    showHoverState,
    writeChecklistElementLocal,
    assignmentId,
    saveAssessmentToolUpdateStatusInRedux,
    setHoverChecklistDetails,
    hoverChecklistDetails,
    setSelectedChecklist,
    selectedChecklist,
    checklistItems,
    createEmptyCheckListItem,
    createEmptyCheckListOptionItem,
    attributes,
    listeners,
    node,
    showChecklistItemDeleteIcon,
    isDragging,
    projectedLineData,
    setIndexToFocusData,
    indexToFocusData,
    disableDragging,
  } = props;

  const [itemLabel, setItemLabel] = useState(label);
  const rowActionsRef = useRef(null);
  const textAreaRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toggleDeleteModal = () => setShowDeleteModal(prev => !prev);
  const onConfirmDelete = () => {
    onItemDeleteClick(item);
    setSelectedChecklist({});
    toggleDeleteModal();
    if (index > 0) setIndexToFocusData({ type: "row", index: index - 1 });
    else setIndexToFocusData({ type: "row", index });
  };
  const isFullyVisible = useIntersectionObserver(node, {
    threshold: 0.5,
  });
  // check if the selected item is in the view or not
  useEffect(() => {
    if (
      !isFullyVisible &&
      !isDragging &&
      selectedChecklist.type === "row" &&
      selectedChecklist.id === id
    ) {
      setSelectedChecklist({});
    }
  }, [isFullyVisible]);

  const refKey = `Criteria_${id}`;
  const disabled = _.includes(id, "NEW");

  const labelContainerClass = classNames({
    [classes.selectedLabelContainer]:
      _.get(selectedChecklist, "id", null) === id,
    [classes.labelContainer]: true,
    [classes.disabled]: disabled,
  });

  const updateChecklistItem = value => {
    setItemLabel(value);
    writeCheckListElement({
      id: id,
      label: value,
      elementDetails: item,
      type: "CHECKLIST_ITEM",
    });
    saveAssessmentToolUpdateStatusInRedux({
      assignmentId,
      toolType: "CHECKLIST",
    });
  };

  const onAddChecklistAfter = async () => {
    setSelectedChecklist({});
    await createEmptyCheckListItem({ putAfterItemId: id });
    setIndexToFocusData({ type: "row", index: index + 1 });
  };
  const onAddChecklistBefore = async () => {
    const { before } = getItemBeforeAndAfter(checklistItems, id);
    setSelectedChecklist({});
    await createEmptyCheckListItem({ putAfterItemId: before?.id || null });
    setIndexToFocusData({ type: "row", index });
  };

  const rowClass = classNames({
    [classes.row]: true,
  });

  const rowActionsClass = classNames({
    [classes.rowActions]: true,
    [classes.activeColumnActions]: _.get(selectedChecklist, "id", null) === id,
  });

  const selectedChecklistIndex = _.findIndex(
    options,
    option => option.id === selectedChecklist.id
  );

  const enableActionPopover =
    node.current &&
    (hoverChecklistDetails.checklistItemId === id ||
      id === selectedChecklist.id) &&
    isFullyVisible &&
    !isDragging;

  const enableActionPopoverOption =
    id === selectedChecklist.id &&
    rowActionsRef.current &&
    isFullyVisible &&
    !isDragging;

  const showOptionDeleteIcon = _.size(options) > 1;

  const optionContainerClass = classNames({
    [classes.optionContainer]: true,
    [classes.selectedOptionContainer]:
      selectedChecklist.type === "row" && selectedChecklist.id === id,
  });

  if (indexToFocusData.index === index && indexToFocusData.type === "row") {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }

  return (
    <div className={rowClass} onFocus={() => onRowFocus(id)}>
      <div
        className={labelContainerClass}
        onMouseEnter={() => {
          setHoverChecklistDetails({
            checklistItemId: id,
          });
        }}
        onMouseLeave={() => {
          setHoverChecklistDetails({});
        }}
      >
        <TextAreaInput
          value={itemLabel}
          textAreaStyles={
            !isDragging
              ? inputStyle.cell
              : mergedStyles.onDraggingTextAreaStyles
          }
          ref={textAreaRef}
          onBlurInputField={() => {
            if (!_.isEmpty(indexToFocusData)) setIndexToFocusData({});
            setFocusedCell(null);
            writeChecklistElementLocal({
              id: id,
              label: itemLabel,
              elementDetails: item,
              type: "CHECKLIST_ITEM",
            });
          }}
          onFocusInputField={() => {
            setFocusedCell(refKey);
            setSelectedChecklist({});
          }}
          updateInputRef={ref => setCellRef({ ref, key: refKey })}
          disabled={disabled}
          placeholder={t("classRoom:type_your_criteria_here")}
          updateInputField={updateChecklistItem}
          className={classes.labelInput}
          minRows={1}
          textAreaContainerStyle={inputStyle.textAreaContainerStyle}
        />

        {enableActionPopover ? (
          <Popover
            isOpen={true}
            align={"center"}
            positions={["left"]}
            containerStyle={styles.rowPopoverContainerStyle}
            boundaryInset={20}
            reposition={true}
            content={() => {
              return (
                <div {...attributes} {...listeners}>
                  <div
                    id={"CHECKLIST_ITEM"}
                    className={rowActionsClass}
                    ref={rowActionsRef}
                    onClick={() => {
                      setSelectedChecklist(
                        _.get(selectedChecklist, "id", null) === id
                          ? {}
                          : {
                              id,
                              type: "row",
                            }
                      );
                    }}
                  >
                    <DotsSixVerticalOutlined
                      size={"xx-small"}
                      style={styles.popoverIconStyle}
                    />
                  </div>
                </div>
              );
            }}
            childrenRef={{ current: node.current }}
            isChildrenRefPassed={true}
          />
        ) : null}
        {enableActionPopoverOption ? (
          <Popover
            isOpen={true}
            align={"center"}
            boundaryInset={50}
            positions={["right"]}
            containerStyle={styles.popoverContainerStyle}
            reposition={true}
            onClickOutside={() => setSelectedChecklist({})}
            content={() => {
              return (
                <div
                  className={classes.popoverRowActions}
                  style={elevations["elevation-2B"]}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<AddColumnOutlined variant="subtle" />}
                    size={"small"}
                    style={styles.transformIconStyle}
                    variant="neutral"
                    type="plain"
                    onClick={onAddChecklistBefore}
                    disabled={disabled || disableDragging}
                  />

                  <IconButton
                    dsVersion="2.0"
                    icon={<AddColumnOutlined variant="subtle" />}
                    style={styles.rotateIconStyle}
                    size={"small"}
                    variant="neutral"
                    type="plain"
                    onClick={onAddChecklistAfter}
                    disabled={disabled || disableDragging}
                  />

                  {showChecklistItemDeleteIcon ? (
                    <>
                      <div className={classes.popoverActionsSeparator} />

                      <IconButton
                        dsVersion="2.0"
                        icon={<DeleteOutlined variant="critical" />}
                        size={"small"}
                        variant="neutral"
                        type="plain"
                        onClick={toggleDeleteModal}
                        disabled={disabled || disableDragging}
                      />
                    </>
                  ) : null}
                </div>
              );
            }}
            childrenRef={{
              current: rowActionsRef.current,
            }}
            isChildrenRefPassed={true}
          />
        ) : null}
        {showDeleteModal ? (
          <DeleteModal
            toggleDeleteModal={toggleDeleteModal}
            onConfirmDelete={onConfirmDelete}
            selectedType={"row"}
          />
        ) : null}
      </div>

      <div className={optionContainerClass}>
        <SortableContext
          disabled={selectedChecklist.type === "row"}
          items={options}
          strategy={rectSortingStrategy}
        >
          {_.map(options, (item, optionIndex) => {
            const shouldDisable = index > 0 || _.includes(item.id, "NEW");
            return (
              <DraggableColumn
                key={item?.id}
                id={item?.id}
                selectedChecklist={selectedChecklist}
                optionIndex={optionIndex}
                criteriaId={id}
                projectedLineData={projectedLineData}
                options={options}
              >
                <CheckListOption
                  setCellRef={setCellRef}
                  setFocusedCell={setFocusedCell}
                  onColumnFocus={onColumnFocus}
                  key={item.id}
                  criteriaId={id}
                  item={item}
                  label={item.label}
                  id={item.id}
                  optionIndex={optionIndex}
                  minOptionCount={minOptionCount}
                  onOptionDeleteClick={() => onOptionDeleteClick(item.id)}
                  writeCheckListElement={writeCheckListElement}
                  index={optionIndex}
                  shouldDisable={shouldDisable}
                  t={t}
                  showHoverState={showHoverState}
                  writeChecklistElementLocal={writeChecklistElementLocal}
                  assignmentId={assignmentId}
                  saveAssessmentToolUpdateStatusInRedux={
                    saveAssessmentToolUpdateStatusInRedux
                  }
                  hoverChecklistDetails={hoverChecklistDetails}
                  selectedChecklist={selectedChecklist}
                  setSelectedChecklist={setSelectedChecklist}
                  setHoverChecklistDetails={setHoverChecklistDetails}
                  criteriaIndex={index}
                  checklistItems={checklistItems}
                  createEmptyCheckListOptionItem={
                    createEmptyCheckListOptionItem
                  }
                  options={options}
                  selectedChecklistIndex={selectedChecklistIndex}
                  showOptionDeleteIcon={showOptionDeleteIcon}
                  isDragging={isDragging}
                  setIndexToFocusData={setIndexToFocusData}
                  indexToFocusData={indexToFocusData}
                />
              </DraggableColumn>
            );
          })}
        </SortableContext>
      </div>
    </div>
  );
};

export default I18nHOC({ resource: ["unitPlan", "common", "classRoom"] })(
  CheckListEditRow
);
